import React, {
  useState,
  useMemo,
  useCallback,
  useContext,
  useEffect,
} from 'react';
import * as Sentry from '@sentry/browser';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  MenuItem,
  FormControl,
  InputLabel,
  InputAdornment,
  FormControlLabel,
  Checkbox,
} from '@mui/material';

import { DateFormat } from '../../../../../../utils/date';
import DialogModal from '../../../../../../components/DialogRoundedModal';
import useToast from '../../../../../hooks/useToast';
import {
  SaveButton,
  CancelButton,
} from '../../../../../../components/Button/ActionButtons';
import {
  paymentRefund,
  getInvoiceRefundDetails,
} from '../../../../../../pages/Onboarding/Subscription/stripe/SubscriptionRemoteRequestHandler';
import FirebaseContext from '../../../../../../context/FirebaseContext';
import { formatCurrencyCents } from '../../../../../../utils/formatters';
import LoadingOverlay from '../../../../LoadingOverlay';
import useComponentMounted from '../../../../../../hooks/useComponentMounted';

import texts from './texts.json';
import {
  StyledSelect,
  Label,
  LabelTitle,
  LabelDescription,
  StyledDialogContent,
  ConfirmContent,
  FieldLabel,
  AmountField,
  HelperRow,
  ErrorText,
  BreakdownPanel,
  BreakdownTitle,
  BreakdownRow,
} from './styles';

const STEP = {
  SELECT: 'select',
  CONFIRM: 'confirm',
};

const RefundInvoiceModal = ({
  isOpen,
  invoice,
  onClose,
  onInvoiceRefunded,
  stripeAccountId,
  productName,
  coachName,
}) => {
  const [details, setDetails] = useState(null);
  const [detailsError, setDetailsError] = useState('');
  const [amountInput, setAmountInput] = useState('');
  const [refundFee, setRefundFee] = useState(true);
  const [reason, setReason] = useState('');
  const [step, setStep] = useState(STEP.SELECT);
  const [isLoading, setIsLoading] = useState(false);

  const { firebase } = useContext(FirebaseContext);
  const { remote } = firebase;
  const { showToast } = useToast();
  const isComponentMountedRef = useComponentMounted();

  const currency = details?.currency || invoice.currency;
  const chargeAmount = details?.amount || 0;
  const refundableCents = details?.refundableAmount || 0;
  const appFeeCents = details?.applicationFeeAmount || 0;
  const hasApplicationFee = appFeeCents > 0 && (details?.applicationFeeRefundable || 0) > 0;

  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }
    let isCurrentRequest = true;

    const loadDetails = async () => {
      setDetails(null);
      setDetailsError('');
      try {
        const response = await getInvoiceRefundDetails({ invoiceId: invoice.id, stripeAccountId, remote });
        if (!isCurrentRequest || !isComponentMountedRef.current) {
          return;
        }
        if (!response || response.error) {
          setDetailsError(response?.error?.message || texts.detailsError);
          return;
        }
        setDetails(response);
        setAmountInput((response.refundableAmount / 100).toFixed(2));
      } catch (error) {
        if (isCurrentRequest && isComponentMountedRef.current) {
          // A rejection here is a runtime string, not a message the endpoint chose to show a coach.
          Sentry.captureException(error);
          setDetailsError(texts.detailsError);
        }
      }
    };

    loadDetails();
    return () => { isCurrentRequest = false; };
  }, [isOpen, invoice.id, stripeAccountId, remote, isComponentMountedRef]);

  // Convert the dollar input into cents and compute the proportional split.
  const {
    enteredCents,
    remainingAfter,
    feePortion,
    coachPortion,
    isOverLimit,
    isPartial,
  } = useMemo(() => {
    const parsed = parseFloat(amountInput);
    const cents = Number.isNaN(parsed) ? 0 : Math.round(parsed * 100);
    // Fee portion = application-fee percentage of the charge applied to the refund amount;
    // the remainder is refunded from the charge (coach payout).
    const feePercentage = hasApplicationFee && chargeAmount > 0 ? appFeeCents / chargeAmount : 0;
    const fee = refundFee && hasApplicationFee
      ? Math.min(Math.round(feePercentage * cents), details?.applicationFeeRefundable || 0)
      : 0;
    return {
      enteredCents: cents,
      remainingAfter: refundableCents - cents,
      feePortion: fee,
      coachPortion: cents - fee,
      isOverLimit: cents > refundableCents,
      isPartial: cents < refundableCents,
    };
  }, [amountInput, refundFee, hasApplicationFee, appFeeCents, chargeAmount, refundableCents, details]);

  const canProceed = !!details && !!reason && enteredCents > 0 && !isOverLimit && !isLoading;

  const handleBack = useCallback(() => {
    setStep(STEP.SELECT);
  }, []);

  const handleConfirm = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await paymentRefund({
        invoiceId: invoice.id,
        stripeAccountId,
        reason,
        amount: enteredCents,
        refundApplicationFee: refundFee,
        remote,
      });

      if (response.error) {
        showToast(response.error.message || texts.errorMessage, { error: true });
      } else {
        showToast(texts.successMessage, { success: true });
        // Signal whether the invoice is now fully refunded so the caller can update eligibility.
        onInvoiceRefunded(invoice.id, remainingAfter <= 0);
      }
    } catch (error) {
      showToast(error.message || texts.errorMessage, { error: true });
    } finally {
      setIsLoading(false);
    }
  }, [
    invoice,
    stripeAccountId,
    reason,
    enteredCents,
    refundFee,
    remainingAfter,
    remote,
    showToast,
    onInvoiceRefunded,
  ]);

  const handleClose = useCallback(() => {
    setStep(STEP.SELECT);
    setReason('');
    setRefundFee(true);
    onClose();
  }, [onClose]);

  const breakdown = (
    <BreakdownPanel>
      <BreakdownTitle>{texts.breakdownTitle}</BreakdownTitle>
      <BreakdownRow>
        <span>{texts.fromCoachPayout}</span>
        <span>{formatCurrencyCents(coachPortion, currency)}</span>
      </BreakdownRow>
      <BreakdownRow>
        <span>{texts.fromApplicationFee}</span>
        <span>{formatCurrencyCents(feePortion, currency)}</span>
      </BreakdownRow>
      <BreakdownRow $total>
        <span>{texts.totalRefund}</span>
        <span>{formatCurrencyCents(enteredCents, currency)}</span>
      </BreakdownRow>
    </BreakdownPanel>
  );

  if (step === STEP.CONFIRM) {
    return (
      <>
        <DialogModal
          open={isOpen}
          fullWidth
          title={isPartial ? texts.confirmPartialTitle : texts.confirmTitle}
          description={texts.confirmDescription}
          onClose={handleClose}
          actionButtons={(
            <>
              <SaveButton
                onClick={handleConfirm}
                icon={null}
                disabled={isLoading}
              >
                {texts.confirm}
              </SaveButton>
              <CancelButton
                onClick={handleBack}
                icon={null}
                disabled={isLoading}
              >
                {texts.back}
              </CancelButton>
            </>
          )}
        >
          <ConfirmContent>
            <Label>
              <LabelTitle>{texts.labels.invoiceTo}</LabelTitle>
              <LabelDescription>{invoice.customer_name}</LabelDescription>
            </Label>
            <Label>
              <LabelTitle>{texts.labels.product}</LabelTitle>
              <LabelDescription>{productName || texts.unknownProduct}</LabelDescription>
            </Label>
            <Label>
              <LabelTitle>{texts.labels.account}</LabelTitle>
              <LabelDescription>{coachName || texts.unknownAccount}</LabelDescription>
            </Label>
            <Label>
              <LabelTitle>{texts.labels.invoiceNumber}</LabelTitle>
              <LabelDescription>{invoice.number}</LabelDescription>
            </Label>
            <Label>
              <LabelTitle>{texts.labels.amount}</LabelTitle>
              <LabelDescription>{formatCurrencyCents(invoice.total, currency)}</LabelDescription>
            </Label>
            <Label>
              <LabelTitle>{texts.labels.refundAmount}</LabelTitle>
              <LabelDescription>{formatCurrencyCents(enteredCents, currency)}</LabelDescription>
            </Label>
            {breakdown}
            <Label>
              <LabelTitle>{texts.labels.reason}</LabelTitle>
              <LabelDescription>{reason}</LabelDescription>
            </Label>
          </ConfirmContent>
        </DialogModal>
        <LoadingOverlay isLoading={isLoading} />
      </>
    );
  }

  return (
    <DialogModal
      open={isOpen}
      title={texts.title}
      fullWidth
      description={texts.description}
      onClose={handleClose}
      actionButtons={(
        <>
          <SaveButton
            onClick={() => setStep(STEP.CONFIRM)}
            icon={null}
            disabled={!canProceed}
          >
            {texts.proceed}
          </SaveButton>
          <CancelButton
            onClick={handleClose}
            icon={null}
            disabled={isLoading}
          >
            {texts.cancel}
          </CancelButton>
        </>
      )}
    >
      <StyledDialogContent>
        <Label>
          <LabelTitle>{texts.labels.invoiceTo}</LabelTitle>
          <LabelDescription>{invoice.customer_name}</LabelDescription>
        </Label>
        <Label>
          <LabelTitle>{texts.labels.invoiceNumber}</LabelTitle>
          <LabelDescription>{invoice.number}</LabelDescription>
        </Label>
        <Label>
          <LabelTitle>{texts.labels.paymentDate}</LabelTitle>
          <LabelDescription>
            {invoice.status_transitions?.paid_at
              ? moment.unix(invoice.status_transitions.paid_at).format(DateFormat.DATE_FORMAT_COMMA_TH)
              : '-'}
          </LabelDescription>
        </Label>
        {!!detailsError && <ErrorText>{detailsError}</ErrorText>}
        {!details && !detailsError && <FieldLabel>{texts.loadingDetails}</FieldLabel>}
        {!!details && (
          <>
            <FieldLabel>{texts.refundAmountLabel}</FieldLabel>
            <AmountField
              value={amountInput}
              onChange={(e) => setAmountInput(e.target.value)}
              type="number"
              variant="outlined"
              disabled={isLoading}
              error={isOverLimit}
              InputProps={{
                startAdornment: <InputAdornment position="start">{currency?.toUpperCase()}</InputAdornment>,
              }}
            />
            <HelperRow>
              <span>{`${formatCurrencyCents(refundableCents, currency)} ${texts.refundableSuffix}`}</span>
              <span>{`${texts.remainingLabel} ${formatCurrencyCents(Math.max(remainingAfter, 0), currency)}`}</span>
            </HelperRow>
            {isOverLimit && <ErrorText>{texts.overLimitError}</ErrorText>}
            {hasApplicationFee && (
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={refundFee}
                    onChange={(e) => setRefundFee(e.target.checked)}
                    disabled={isLoading}
                  />
                )}
                label={texts.refundFeeCheckbox}
              />
            )}
            {breakdown}
            <FormControl fullWidth variant="outlined" disabled={isLoading}>
              <InputLabel>{texts.reasonPlaceholder}</InputLabel>
              <StyledSelect
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                label={texts.reasonPlaceholder}
              >
                {texts.reasons.map((r) => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </>
        )}
      </StyledDialogContent>
    </DialogModal>
  );
};

RefundInvoiceModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  invoice: PropTypes.object.isRequired,
  stripeAccountId: PropTypes.string,
  productName: PropTypes.string,
  coachName: PropTypes.string,
  onInvoiceRefunded: PropTypes.func.isRequired,
};

RefundInvoiceModal.defaultProps = {
  stripeAccountId: undefined,
  productName: '',
  coachName: '',
};

export default RefundInvoiceModal;
